/*
  Shared transform-based slider for BS sections
  (used by bs-slider-icon and bs-editorial).

  Markup contract:
  - Root element:        [data-bs-slider]
  - Sliding track:       [data-bs-slider-track] (optional data-per-view, default 1)
  - Slides/cells:        [data-bs-slider-slide]
  - Arrow buttons:       [data-bs-slider-previous] / [data-bs-slider-next]

  The track must be a flex or grid container whose column-gap (if any)
  is readable via getComputedStyle.
*/
(() => {
  if (window.bsSliderLoaded) return;
  window.bsSliderLoaded = true;

  const initSlider = (root) => {
    if (!root || root.dataset.initialized === 'true') return;

    const track = root.querySelector('[data-bs-slider-track]');
    const slides = Array.from(root.querySelectorAll('[data-bs-slider-slide]'));
    const previousButtons = root.querySelectorAll('[data-bs-slider-previous]');
    const nextButtons = root.querySelectorAll('[data-bs-slider-next]');

    const perView = parseInt(track && track.dataset.perView, 10) || 1;

    if (!track || slides.length <= perView) return;

    root.dataset.initialized = 'true';

    const maxIndex = slides.length - perView;
    let currentIndex = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchCurrentX = 0;
    let touchCurrentY = 0;
    let isDragging = false;

    const step = () => {
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return slides[0].getBoundingClientRect().width + gap;
    };

    const render = () => {
      track.style.transform = `translate3d(-${currentIndex * step()}px, 0, 0)`;

      slides.forEach((slide, index) => {
        const isVisible = index >= currentIndex && index < currentIndex + perView;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
    };

    const goTo = (index) => {
      if (index < 0) {
        currentIndex = maxIndex;
      } else if (index > maxIndex) {
        currentIndex = 0;
      } else {
        currentIndex = index;
      }

      render();
    };

    previousButtons.forEach((button) => {
      button.addEventListener('click', () => goTo(currentIndex - 1));
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => goTo(currentIndex + 1));
    });

    track.addEventListener(
      'touchstart',
      (event) => {
        if (!event.touches[0]) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
        touchCurrentX = touchStartX;
        touchCurrentY = touchStartY;
        isDragging = true;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchmove',
      (event) => {
        if (!isDragging || !event.touches[0]) return;
        touchCurrentX = event.touches[0].clientX;
        touchCurrentY = event.touches[0].clientY;
      },
      { passive: true }
    );

    track.addEventListener('touchend', () => {
      if (!isDragging) return;

      const horizontalDistance = touchCurrentX - touchStartX;
      const verticalDistance = Math.abs(touchCurrentY - touchStartY);

      isDragging = false;

      if (
        Math.abs(horizontalDistance) < 45 ||
        Math.abs(horizontalDistance) <= verticalDistance
      ) {
        return;
      }

      if (horizontalDistance < 0) {
        goTo(currentIndex + 1);
      } else {
        goTo(currentIndex - 1);
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') goTo(currentIndex - 1);
      if (event.key === 'ArrowRight') goTo(currentIndex + 1);
    });

    window.addEventListener('resize', render);

    render();
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('[data-bs-slider]').forEach(initSlider);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /* Theme editor: re-init sliders inside a re-rendered section. */
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
